import { api } from '../../lib/api';
import type { ClubwiseJoinRow, ClubwiseLeadRow } from '../../lib/clubwise/csv';
import type { ClubwiseUploadAnalysis } from '../../lib/clubwise/upload';

// ClubWise upload — the client's API calls. The body is the browser's
// WHITELISTED projection (member number, status, source, contact type, and for
// joiners membership type + joining date) and nothing else; the server refuses
// any other key.

export interface ClubwiseUploadBody {
  month: string;
  /** YYYY-MM-DD the exports run to; the whole month if omitted. */
  dataTo?: string | null;
  leads: ClubwiseLeadRow[];
  joins: ClubwiseJoinRow[];
}

export interface ClubwiseUploadSummary {
  uploadId: string;
  month: string;
  /** YYYY-MM-DD the upload runs to; null = the whole month. */
  dataTo?: string | null;
  leadRows: number;
  joinRows: number;
  cohortJoins: number;
  digitalLeads: number;
  uploadedAt: string;
  uploadedByEmail: string;
}

export interface ClubwiseStatus {
  isClubwiseBrand: boolean;
  /** The club's own time zone (its brand's), for "today" and the data-up-to default. */
  timeZone: string;
  clubwiseCode: string | null;
  accountName: string;
  uploads: ClubwiseUploadSummary[];
  uploadsError: string | null;
}

export interface ClubwisePreview {
  clubCode: string;
  accountName: string;
  month: string;
  analysis: ClubwiseUploadAnalysis;
  existingUpload: {
    uploadedAt: string;
    uploadedByEmail: string;
    leadRows: number;
    joinRows: number;
  } | null;
  existingUploadError: string | null;
}

export interface ClubwiseCommitResult {
  uploadId: string;
  clubCode: string;
  month: string;
  counts: ClubwiseUploadAnalysis['counts'];
  replacedExisting: boolean;
  auditRecorded: boolean;
}

export const getClubwiseStatus = (accountId: string): Promise<ClubwiseStatus> =>
  api<ClubwiseStatus>(`/api/clubwise/${accountId}/status`);

export const previewClubwiseUpload = (
  accountId: string,
  body: ClubwiseUploadBody,
): Promise<ClubwisePreview> =>
  api<ClubwisePreview>(`/api/clubwise/${accountId}/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const commitClubwiseUpload = (
  accountId: string,
  body: ClubwiseUploadBody,
): Promise<ClubwiseCommitResult> =>
  api<ClubwiseCommitResult>(`/api/clubwise/${accountId}/commit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
